import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { TreatmentPlan, TreatmentPlanInput, TreatmentGoal } from '../models/TreatmentPlan';

/**
 * Treatment plans — clients/{clientId}/treatmentPlans/{planId}.
 * The plan is an embedded tree (goals[] > targets[] > programs[]); goals may
 * carry an assessmentRef linking back to a clients/{id}/assessments doc.
 */
export class TreatmentPlanService {
  private static instance: TreatmentPlanService;
  private constructor() {}
  static getInstance(): TreatmentPlanService {
    if (!TreatmentPlanService.instance) TreatmentPlanService.instance = new TreatmentPlanService();
    return TreatmentPlanService.instance;
  }

  private col(clientId: string) {
    return collection(db, 'clients', clientId, 'treatmentPlans');
  }

  async create(clientId: string, companyId: string, input: TreatmentPlanInput): Promise<string> {
    const ref = doc(this.col(clientId));
    const now = Timestamp.now();
    await setDoc(ref, {
      id: ref.id,
      clientId,
      companyId,
      createdBy: input.createdBy,
      status: input.status ?? 'draft',
      effectiveDate: Timestamp.fromDate(input.effectiveDate),
      reviewDate: Timestamp.fromDate(input.reviewDate),
      previousPlanId: input.previousPlanId ?? null,
      goals: this.goalsToFirestore(input.goals),
      createdAt: now,
      updatedAt: now,
    });
    return ref.id;
  }

  async update(
    clientId: string,
    planId: string,
    updates: Partial<Pick<TreatmentPlan, 'goals' | 'status' | 'reviewDate' | 'effectiveDate'>>
  ): Promise<void> {
    const patch: Record<string, any> = { updatedAt: Timestamp.now() };
    if (updates.goals) patch.goals = this.goalsToFirestore(updates.goals);
    if (updates.status) patch.status = updates.status;
    if (updates.reviewDate) patch.reviewDate = Timestamp.fromDate(updates.reviewDate);
    if (updates.effectiveDate) patch.effectiveDate = Timestamp.fromDate(updates.effectiveDate);
    await updateDoc(doc(this.col(clientId), planId), patch);
  }

  async getByClient(clientId: string): Promise<TreatmentPlan[]> {
    const q = query(this.col(clientId), orderBy('effectiveDate', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => this.map(clientId, d.id, d.data()));
  }

  async getActivePlan(clientId: string): Promise<TreatmentPlan | null> {
    const q = query(this.col(clientId), where('status', '==', 'active'));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const plans = snap.docs.map((d) => this.map(clientId, d.id, d.data()));
    plans.sort((a, b) => b.effectiveDate.getTime() - a.effectiveDate.getTime());
    return plans[0];
  }

  async getPlan(clientId: string, planId: string): Promise<TreatmentPlan | null> {
    const snap = await getDoc(doc(this.col(clientId), planId));
    if (!snap.exists()) return null;
    return this.map(clientId, snap.id, snap.data());
  }

  /* --------------------------- date mapping --------------------------- */

  private toDate(v: any): Date | null {
    return v && typeof v.toDate === 'function' ? v.toDate() : null;
  }
  private ts(d: Date | null | undefined): Timestamp | null {
    return d ? Timestamp.fromDate(d) : null;
  }

  private goalsToFirestore(goals: TreatmentGoal[]): any[] {
    return (goals || []).map((g) => ({
      ...g,
      createdDate: this.ts(g.createdDate),
      targetDate: this.ts(g.targetDate),
      assessmentRef: g.assessmentRef
        ? { ...g.assessmentRef, administeredDate: this.ts(g.assessmentRef.administeredDate) }
        : null,
      targets: (g.targets || []).map((t) => ({ ...t })),
    }));
  }

  private goalsFromFirestore(goals: any[]): TreatmentGoal[] {
    return (goals || []).map((g) => ({
      goalId: g.goalId,
      description: g.description || '',
      status: g.status || 'active',
      createdDate: this.toDate(g.createdDate) || new Date(),
      targetDate: this.toDate(g.targetDate),
      assessmentRef: g.assessmentRef
        ? { ...g.assessmentRef, administeredDate: this.toDate(g.assessmentRef.administeredDate) || new Date() }
        : null,
      targets: Array.isArray(g.targets) ? g.targets : [],
    }));
  }

  private map(clientId: string, id: string, data: any): TreatmentPlan {
    return {
      id,
      clientId: data.clientId || clientId,
      companyId: data.companyId || '',
      createdBy: data.createdBy || '',
      status: data.status || 'draft',
      effectiveDate: this.toDate(data.effectiveDate) || new Date(),
      reviewDate: this.toDate(data.reviewDate) || new Date(),
      previousPlanId: data.previousPlanId ?? null,
      goals: this.goalsFromFirestore(data.goals),
      createdAt: this.toDate(data.createdAt) || new Date(),
      updatedAt: this.toDate(data.updatedAt) || new Date(),
    };
  }
}

export const treatmentPlanService = TreatmentPlanService.getInstance();
